import { useCallback, useMemo } from "react";
import useTransientStore from "../data/transcientStore";
import { usePermissions } from "./usePermissions";

export interface AddressSystemVisibilityItem<TId extends string = string> {
  id: TId;
  requiredPermission?: string;
  /**
   * When false, a missing ENABLE_* client setting hides the item (opt-in).
   * Default true preserves legacy Reports/Settings behavior (missing = visible).
   */
  defaultEnabled?: boolean;
}

function buildVisibilitySettingName(itemId: string) {
  return `ENABLE_${itemId.replace(/-/g, "_").toUpperCase()}`;
}

function isPageEnabled(
  clientSystemSettings: ReadonlyMap<string, string>,
  item: AddressSystemVisibilityItem,
) {
  const settingValue = clientSystemSettings.get(buildVisibilitySettingName(item.id));
  if (!settingValue) {
    return item.defaultEnabled !== false;
  }

  const normalizedValue = settingValue.trim().toLowerCase();
  return (
    normalizedValue !== "false" &&
    normalizedValue !== "0" &&
    normalizedValue !== "no"
  );
}

function isPageLinkVisible<TItem extends AddressSystemVisibilityItem>(
  item: TItem,
  clientSystemSettings: ReadonlyMap<string, string>,
  checkAccess: (functionCode: string) => boolean,
) {
  return (
    isPageEnabled(clientSystemSettings, item) &&
    (!item.requiredPermission || checkAccess(item.requiredPermission))
  );
}

export function useAddressSystemPageAccess<
  TId extends string,
  TItem extends AddressSystemVisibilityItem<TId>,
>(items: readonly TItem[]) {
  const clientSystemSettings = useTransientStore(
    (state) => state.clientSystemSettings,
  );
  const { checkAccess } = usePermissions();

  const itemsById = useMemo(
    () => new Map(items.map((item) => [item.id, item] as const)),
    [items],
  );

  const visibleItems = useMemo(
    () =>
      items.filter((item) =>
        isPageLinkVisible(item, clientSystemSettings, checkAccess),
      ),
    [items, clientSystemSettings, checkAccess],
  );

  const isItemVisible = useCallback(
    (itemId: TId) => {
      const item = itemsById.get(itemId);
      return (
        item != null &&
        isPageLinkVisible(item, clientSystemSettings, checkAccess)
      );
    },
    [itemsById, clientSystemSettings, checkAccess],
  );

  return {
    visibleItems,
    isItemVisible,
  };
}
